import { useMemo } from "react";
import { DM_TYPES } from "../lib/s1000dData";
import { useEstimate } from "../store/EstimateContext";
import { num, fmtHours } from "../lib/calculations";
import HelpIconButton from "./HelpIconButton";

export default function ReviewsUpdatesTab() {
  const { estimate, totals, review, combined, updateReviewPlan, formatCurrency } = useEstimate();
  const plan = estimate.reviewPlan || { percentages: [25, 50, 75] };
  const pcts = useMemo(() => plan.percentages || [25, 50, 75], [plan.percentages]);
  const blendedRate = totals.blendedRate || 0;

  // Build rows: one per DM type that has hours, plus Illustrations
  const rows = useMemo(() => {
    const result = [];
    for (const t of DM_TYPES) {
      const hrs = num(totals.perTypeHours?.[t.code]);
      if (hrs > 0) {
        result.push({ key: t.code, name: t.name, source: "Data modules", hours: hrs });
      }
    }
    const illusHrs = num(totals.illusHours);
    if (illusHrs > 0) {
      result.push({ key: "illus", name: "Illustrations", source: "Illustrations", hours: illusHrs });
    }
    return result;
  }, [totals.perTypeHours, totals.illusHours]);

  // Compute review hours and costs per row
  const computed = useMemo(() => {
    let grandReviewHrs = 0;
    let grandCost = 0;
    const rowData = rows.map((r) => {
      const updates = pcts.map((p) => r.hours * (num(p) / 100));
      const reviewHrs = updates.reduce((s, v) => s + v, 0);
      const cost = reviewHrs * blendedRate;
      grandReviewHrs += reviewHrs;
      grandCost += cost;
      return { ...r, updates, reviewHrs, cost };
    });
    return { rowData, grandReviewHrs, grandCost };
  }, [rows, pcts, blendedRate]);

  const setPct = (idx, val) => {
    const next = [...pcts];
    next[idx] = num(val);
    updateReviewPlan("percentages", next);
  };

  const fmtNum = (v) => {
    const n = num(v);
    return n % 1 === 0 ? n.toLocaleString() : n.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  };

  // Role breakdown for transparency
  const roles = estimate.roles || [];

  return (
    <section className="space-y-4" data-testid="reviews-updates-tab">
      {/* Section header with grand total card */}
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#8A887F]">
            03 · Review cycle costing
          </p>
          <h2 className="text-xl font-extrabold text-[#111] mt-1 flex items-center gap-2">
            Review &amp; Update
            <HelpIconButton anchor="ch-reviews" testId="help-icon-reviews" />
          </h2>
          <p className="text-sm text-[#5F5E5A] mt-1 max-w-2xl leading-relaxed">
            Hours roll up automatically from Data Modules and Illustrations. The blended hourly rate is derived from the roles defined in Plan &gt; Roles &amp; Rates, weighted by each role's effort percentage.
          </p>
        </div>
        <div className="bg-white border border-[#E7E6E2] rounded-xl px-5 py-3 min-w-[180px]" data-testid="reviews-grand-total-card">
          <div className="text-[11px] font-bold uppercase tracking-[0.05em] text-[#8A887F]">Review total</div>
          <div className="text-[22px] font-extrabold text-[#5A2FC7] mt-0.5" data-testid="reviews-grand-total">
            {formatCurrency(computed.grandCost)}
          </div>
        </div>
      </div>

      {/* Role-derived blended rate bar */}
      <div className="bg-white border border-[#E7E6E2] rounded-xl px-5 py-3" data-testid="reviews-rate-bar">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-[#8A887F] font-semibold">Blended rate</span>
            <span className="text-lg font-extrabold text-[#111]" data-testid="reviews-blended-rate">{formatCurrency(blendedRate)}/hr</span>
          </div>
          <p className="text-xs text-[#8A887F]">Derived from Plan › Roles &amp; Rates · Review pass percentages are editable per column.</p>
        </div>
        {roles.length > 0 && (
          <div className="flex flex-wrap gap-3 mt-1">
            {roles.map((r, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 text-xs bg-[#F3F3F1] text-[#5F5E5A] px-2.5 py-1 rounded-full" data-testid={`reviews-role-${i}`}>
                <span className="font-semibold text-[#111]">{r.name || `Role ${i+1}`}</span>
                {formatCurrency(num(r.hourlyRate))}/hr · {num(r.effortPercent)}%
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E7E6E2] rounded-xl overflow-hidden" data-testid="reviews-table-container">
        <table className="w-full border-collapse text-sm" data-testid="reviews-table">
          <colgroup>
            <col className="w-[28%]" />
            <col className="w-[14%]" />
            <col className="w-[14%]" />
            <col className="w-[14%]" />
            <col className="w-[14%]" />
            <col className="w-[16%]" />
          </colgroup>
          <thead>
            <tr className="border-b border-[#E7E6E2]">
              <th rowSpan={2} className="text-left text-[11px] font-bold uppercase tracking-[0.04em] text-[#8A887F] px-4 py-3 bg-[#FAFAFA] border-b border-[#E7E6E2]">
                DMC / ICN type
              </th>
              <th rowSpan={2} className="text-right text-[11px] font-bold uppercase tracking-[0.04em] text-[#8A887F] px-4 py-3 bg-[#FAFAFA] border-b border-[#E7E6E2]">
                Total hours
              </th>
              <th colSpan={3} className="text-center text-[11px] font-bold uppercase tracking-[0.04em] text-[#8A887F] px-4 py-2 bg-[#FAFAFA]">
                Updates
              </th>
              <th rowSpan={2} className="text-right text-[11px] font-bold uppercase tracking-[0.04em] text-[#8A887F] px-4 py-3 bg-[#FAFAFA] border-b border-[#E7E6E2]">
                Total cost
              </th>
            </tr>
            <tr className="border-b border-[#E7E6E2] bg-[#FAFAFA]">
              {pcts.map((p, i) => (
                <th key={i} className="text-center px-4 py-2">
                  <div className="inline-flex items-center gap-0.5">
                    <input
                      data-testid={`reviews-pct-${i}`}
                      type="number"
                      value={p}
                      min="0"
                      max="100"
                      onChange={(e) => setPct(i, e.target.value)}
                      className="w-12 text-center border border-[#E7E6E2] rounded-md px-1 py-1 text-sm font-bold text-[#111] bg-white focus:outline-none focus:ring-2 focus:ring-[#6D3FE0]"
                    />
                    <span className="text-[11px] text-[#8A887F] font-bold">%</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {computed.rowData.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-[#B4B2A9]">
                  No data modules or illustrations with hours. Add quantities in the Data Modules or Illustrations tabs.
                </td>
              </tr>
            ) : (
              computed.rowData.map((r) => (
                <tr key={r.key} className="border-b border-[#F3F3F1] hover:bg-[#FAFAFA] transition-colors" data-testid={`reviews-row-${r.key}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#111]">{r.name}</div>
                    <div className="text-[11px] text-[#B4B2A9]">from {r.source}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[#111]" data-testid={`reviews-hours-${r.key}`}>
                    {fmtNum(r.hours)}
                  </td>
                  {r.updates.map((u, i) => (
                    <td key={i} className="px-4 py-3 text-center font-mono text-[#B4B2A9]" data-testid={`reviews-upd-${r.key}-${i}`}>
                      {fmtNum(u)}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right font-mono font-semibold text-[#111]" data-testid={`reviews-cost-${r.key}`}>
                    {formatCurrency(r.cost)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer bar */}
      <div className="flex items-center justify-between bg-white border border-[#E7E6E2] rounded-xl px-5 py-3 text-sm" data-testid="reviews-footer">
        <span className="text-[#5F5E5A] font-semibold">Review total</span>
        <span className="font-mono font-bold text-[#111]" data-testid="reviews-footer-total">
          {fmtNum(computed.grandReviewHrs)} review hrs · {formatCurrency(computed.grandCost)}
        </span>
      </div>

      <p className="text-xs text-[#B4B2A9] leading-relaxed max-w-3xl">
        Total hours per DM type pull the sum of simple, intermediate and complex hours from Data Modules; the Illustrations row sums simple, intermediate and complex illustration hours. The blended hourly rate is the weighted average of all role rates by effort percentage from Plan &gt; Roles &amp; Rates. All fields except review percentages are read-only.
      </p>
    </section>
  );
}
